import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Button,
  InputGroup,
  Form,
  Row,
  Col,
  Container,
  Spinner,
} from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { FaFileExcel, FaSearch, FaBoxOpen } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DataTable from 'react-data-table-component';
import './AdminLayout.css';
import BASE_URL from '../config';

// ✅ Axios instance with interceptor for authentication
const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const AdminReports = () => {
  // Helper function to get first day of current month in YYYY-MM-DD format
  const getFirstDayOfMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedShowroom, setSelectedShowroom] = useState(null);
  const [showrooms, setShowrooms] = useState([]);
  const [dateFrom, setDateFrom] = useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getCurrentDate());
  const [selectedRole, setSelectedRole] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [lastPage, setLastPage] = useState(1);
  const isFetchingRef = useRef(false);

  const statusOptions = [
    'pending',
    'assigned',
    'accepted',
    'rejected',
    'picked_up',
    'in_transit',
    'delivered',
    'returned',
    'cancelled',
    'unfulfilled',
  ].map((s) => ({ value: s, label: s.replace('_', ' ').toUpperCase() }));

  // Fetch a single page of orders
  const fetchOrdersPage = async (page, rowsPerPage, dateFilters = null) => {
    const params = {
      per_page: rowsPerPage,
    };

    // Add date filters if provided
    if (dateFilters) {
      if (dateFilters.dateFrom) {
        params.start_date = dateFilters.dateFrom;
      }
      if (dateFilters.dateTo) {
        params.end_date = dateFilters.dateTo;
      }
    }

    const res = await authAxios.get(`/orders/history?page=${page}`, {
      params,
    });
    const responseData = res.data?.data;
    const ordersData = Array.isArray(responseData?.data)
      ? responseData.data
      : Array.isArray(responseData)
      ? responseData
      : [];

    // Extract pagination metadata
    const totalCount = responseData?.total;
    const lastPageNum =
      responseData?.last_page ||
      responseData?.meta?.last_page ||
      res.data?.last_page;
    const currentPageNum =
      responseData?.current_page ||
      responseData?.meta?.current_page ||
      res.data?.current_page ||
      page;

    return {
      ordersData,
      totalCount,
      lastPageNum,
      currentPageNum,
      hasMore: lastPageNum
        ? page < lastPageNum
        : ordersData.length === rowsPerPage,
    };
  };

  // Fetch all orders page by page until last page
  const fetchAllOrdersSequentially = async (dateFilters = null) => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setLoading(true);
    setAllOrders([]);
    setCurrentPage(1);
    setHasMore(true);

    try {
      let allOrdersList = [];
      let page = 1;
      let lastPageNum = null;
      let totalCount = 0;

      // Fetch first page to get metadata
      const firstPageData = await fetchOrdersPage(page, perPage, dateFilters);
      allOrdersList = [...firstPageData.ordersData];
      lastPageNum = firstPageData.lastPageNum;
      totalCount = firstPageData.totalCount || allOrdersList.length;

      // Update state with first page data
      setAllOrders(allOrdersList);
      setCurrentPage(page);
      setTotalRows(totalCount);
      if (lastPageNum) {
        setLastPage(lastPageNum);
      }

      // If we have last_page info, fetch remaining pages
      if (lastPageNum && lastPageNum > 1) {
        for (page = 2; page <= lastPageNum; page++) {
          try {
            const pageData = await fetchOrdersPage(page, perPage, dateFilters);
            if (pageData.ordersData.length === 0) {
              break;
            }
            allOrdersList = [...allOrdersList, ...pageData.ordersData];
            setAllOrders([...allOrdersList]);
            setCurrentPage(page);
          } catch (err) {
            console.error(`Error fetching orders page ${page}:`, err);
            break;
          }
        }
      } else if (firstPageData.hasMore && !lastPageNum) {
        // If we don't have last_page info, continue fetching until we get an empty page
        page = 2;
        while (true) {
          try {
            const pageData = await fetchOrdersPage(page, perPage, dateFilters);
            if (pageData.ordersData.length === 0) {
              break;
            }
            allOrdersList = [...allOrdersList, ...pageData.ordersData];
            setAllOrders([...allOrdersList]);
            setCurrentPage(page);

            // If we got fewer items than per_page, it's likely the last page
            if (pageData.ordersData.length < perPage) {
              break;
            }
            page++;
          } catch (err) {
            console.error(`Error fetching orders page ${page}:`, err);
            break;
          }
        }
      }

      setHasMore(false);
      setLastPage(page);
      setTotalRows(allOrdersList.length);
      console.log(
        `Finished fetching all orders. Total: ${allOrdersList.length}`
      );
    } catch (err) {
      console.error('Error fetching all orders:', err);
      setAllOrders([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // ✅ Fetch Showrooms
  const fetchShowrooms = async () => {
    try {
      const response = await authAxios.get('/showrooms');
      console.log('✅ Showrooms API response:', response.data);

      // Extract the correct list of showrooms
      const rawList =
        response.data?.data?.data?.data ||
        response.data?.data?.data ||
        response.data?.data ||
        [];

      console.log('✅ Parsed raw showroom list:', rawList);

      // Convert to react-select format
      const formatted = rawList.map((s) => ({
        value: s.name, // what you’ll filter by
        label: s.name, // what will display in dropdown
        id: s.id, // optional, if you need it later
      }));

      console.log('✅ Formatted showroom options:', formatted);
      setShowrooms(formatted);
    } catch (error) {
      console.error('❌ Failed to fetch showrooms:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Use default date filters (first day of month to current date)
      const dateFilters = {
        dateFrom: getFirstDayOfMonth(),
        dateTo: getCurrentDate(),
      };
      await fetchAllOrdersSequentially(dateFilters);
    };
    fetchData();
    fetchShowrooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh orders after search/filter
  const refreshOrders = (dateFilters = null) => {
    fetchAllOrdersSequentially(dateFilters);
  };

  // Handle search/fetch button click
  const handleSearch = async () => {
    const dateFilters = {
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    };
    await fetchAllOrdersSequentially(dateFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    // Reset to default dates (first day of month to current date)
    setDateFrom(getFirstDayOfMonth());
    setDateTo(getCurrentDate());
    setSearch('');
    setSelectedStatus(null);
    setSelectedShowroom(null);
    setSelectedRole(null);
    // Refetch with default date filters
    const dateFilters = {
      dateFrom: getFirstDayOfMonth(),
      dateTo: getCurrentDate(),
    };
    fetchAllOrdersSequentially(dateFilters);
  };

  // Removed Intersection Observer - now fetching all pages sequentially

  // ✅ Helper functions
  // Helper to format date to YYYY-MM-DD for grouping/sorting (from the SQL)
  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  const formatDateTime12Hr = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const mins = minutes < 10 ? `0${minutes}` : minutes;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year} ${hours}:${mins} ${ampm}`;
  };

  const calculateTimeTaken = (pickedUpTime, deliveredTime) => {
    if (!pickedUpTime || !deliveredTime) return '';
    const diffMs = new Date(deliveredTime) - new Date(pickedUpTime);
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHrs}h ${diffMins}m`;
  };

  // ✅ Filters (client-side filtering for search, status, showroom, and role)
  // Note: Date filtering is now done server-side via API
  useEffect(() => {
    let filtered = allOrders;

    if (search) {
      filtered = filtered.filter(
        (o) =>
          o.id?.toString().includes(search) ||
          o.type?.toLowerCase().includes(search.toLowerCase()) ||
          o.status?.toLowerCase().includes(search.toLowerCase()) ||
          o.driver?.name?.toLowerCase().includes(search.toLowerCase()) ||
          (o.customer?.name || o.vendor?.name)
            ?.toLowerCase()
            .includes(search.toLowerCase())
      );
    }

    if (selectedStatus?.value)
      filtered = filtered.filter((o) => o.status === selectedStatus.value);

    if (selectedShowroom && selectedShowroom.value)
      filtered = filtered.filter(
        (o) => o.showroom?.name === selectedShowroom.value
      );

    // Date filtering is now done server-side when Search button is clicked
    // Removed client-side date filtering

    if (selectedRole === 'driver') filtered = filtered.filter((o) => o.driver);
    else if (selectedRole === 'vendor')
      filtered = filtered.filter((o) => o.vendor);
    else if (selectedRole === 'customer')
      filtered = filtered.filter((o) => o.customer);

    setOrders(filtered);
  }, [allOrders, search, selectedStatus, selectedShowroom, selectedRole]);

  // --- START: Grouped Vendor Data Logic ---
  const groupedByVendor = useMemo(() => {
    if (selectedRole !== 'vendor') return [];
    const groups = {};
    orders.forEach((o) => {
      // Logic for: AND o.vendor_id IS NOT NULL
      if (!o.vendor) return;

      const vendorName = o.vendor.name || 'N/A';

      if (!groups[vendorName]) groups[vendorName] = [];
      groups[vendorName].push(o);
    });

    return Object.entries(groups)
      .map(([vendorName, vendorOrders]) => {
        const first = vendorOrders[0];

        // Logic for: WHERE o.status = 'delivered' and COUNT(o.id)
        const deliveredOrders = vendorOrders.filter(
          (order) => order.status === 'delivered'
        );

        return {
          vendorName, // v.name AS vendor_name
          branch: first?.showroom?.name || 'N/A',
          date: formatDate(first?.created_at),
          deliveriesCompleted: deliveredOrders.length, // COUNT(o.id)
        };
      })
      .sort((a, b) => b.deliveriesCompleted - a.deliveriesCompleted);
  }, [orders, selectedRole]);
  // --- END: Grouped Vendor Data Logic ---

  // --- START: New Grouped Driver (Branch/Date/Driver) Logic ---
  const groupedByDriver = useMemo(() => {
    if (selectedRole !== 'driver') return [];

    // 1. Filter and Group
    const groups = {};
    orders.forEach((o) => {
      // WHERE o.driver_id IS NOT NULL AND o.showroom_id IS NOT NULL AND o.status = 'delivered'
      if (!o.driver || !o.showroom || o.status !== 'delivered') return;

      // Grouping key: branch_name + delivery_date + driver_name
      const branchName = o.showroom.name; // s.name
      // Use updated_at for delivery date (as per SQL logic)
      const deliveryDate = formatDeliveryDate(o.updated_at); // DATE(o.updated_at)
      const driverName = o.driver.name; // u.name

      const key = `${branchName}|${deliveryDate}|${driverName}`;

      if (!groups[key]) groups[key] = [];
      groups[key].push(o);
    });

    // 2. Format Results (Counting)
    const groupedData = Object.entries(groups).map(([key, groupOrders]) => {
      const [branchName, deliveryDate, driverName] = key.split('|');

      // COUNT(o.id) AS deliveries_count
      const deliveriesCount = groupOrders.length;

      // Calculate Average Delivery Time (Time taken for these N deliveries)
      const totalMinutes = groupOrders.reduce((acc, order) => {
        const pickedUpLog = order.status_logs?.find(
          (l) => l.to_status === 'picked_up'
        );
        // Note: For delivered orders, updated_at is typically the delivery time,
        // but using status_logs for accuracy (since we already did this).
        const deliveredLog = order.status_logs?.find(
          (l) => l.to_status === 'delivered'
        );
        if (pickedUpLog && deliveredLog) {
          const start = new Date(pickedUpLog.created_at);
          const end = new Date(deliveredLog.created_at);
          return acc + (end - start) / (1000 * 60);
        }
        return acc;
      }, 0);

      const avgMinutes =
        deliveriesCount > 0 ? totalMinutes / deliveriesCount : 0;
      const avgTime =
        avgMinutes > 0
          ? `${Math.floor(avgMinutes / 60)}h ${Math.round(avgMinutes % 60)}m`
          : 'N/A';

      return {
        branchName,
        deliveryDate: formatDate(deliveryDate), // Format for display
        driverName,
        deliveriesCount, // deliveries_count
        avgDeliveryTime: avgTime,
        // Helper fields for sorting (using full date string)
        sortBranch: branchName,
        sortDate: deliveryDate,
      };
    });

    // 3. Sorting (ORDER BY branch_name ASC, delivery_date ASC, deliveries_count DESC)
    return groupedData.sort((a, b) => {
      // branch_name ASC
      if (a.sortBranch < b.sortBranch) return -1;
      if (a.sortBranch > b.sortBranch) return 1;

      // delivery_date ASC
      if (a.sortDate < b.sortDate) return -1;
      if (a.sortDate > b.sortDate) return 1;

      // deliveries_count DESC
      return b.deliveriesCount - a.deliveriesCount;
    });
  }, [orders, selectedRole]);
  // --- END: New Grouped Driver (Branch/Date/Driver) Logic ---

  // ✅ Export to Excel
  const handleExportToExcel = () => {
    if (!orders.length) {
      alert('No data to export');
      return;
    }

    let exportData = [];
    if (selectedRole === 'driver') {
      // Export logic for new Driver report format
      exportData = groupedByDriver.map((item) => ({
        'Branch Name': item.branchName,
        'Delivery Date': item.deliveryDate,
        'Driver Name': item.driverName,
        'No. of Deliveries Completed': item.deliveriesCount,
        'Average Delivery Time': item.avgDeliveryTime,
      }));
    } else if (selectedRole === 'vendor') {
      // Export logic for Vendor report
      exportData = groupedByVendor.map((item) => ({
        'Vendor Name': item.vendorName,
        Branch: item.branch,
        Date: item.date,
        'No. of Deliveries Completed': item.deliveriesCompleted,
      }));
    } else {
      // Export logic for All Orders
      exportData = orders.map((row) => ({
        'PO Number': row.po_number || 'N/A',
        'Order Type': row.type,
        Showroom: row.showroom?.name || 'N/A',
        Driver: row.driver?.name || 'N/A',
        'Customer/Vendor': row.customer?.name || row.vendor?.name || 'N/A',
        Status: row.status,
        'Order Created At': formatDateTime12Hr(row.created_at),
        'Order Updated At': row.updated_at
          ? formatDateTime12Hr(row.updated_at)
          : '',
        'Time Taken for Delivery': calculateTimeTaken(
          row.status_logs?.find((l) => l.to_status === 'picked_up')?.created_at,
          row.status_logs?.find((l) => l.to_status === 'delivered')?.created_at
        ),
      }));
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], {
        type: 'application/octet-stream',
      }),
      `report_${selectedRole || 'all'}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };

  const vendorColumns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      width: '60px',
      compact: true,
    },
    {
      name: 'Vendor Name',
      selector: (row) => row.vendorName,
      sortable: true,
      width: '150px',
      wrap: true,
    },
    {
      name: 'Branch',
      selector: (row) => row.branch,
      sortable: true,
      width: '120px',
      wrap: true,
    },
    {
      name: 'Date',
      selector: (row) => row.date,
      sortable: true,
      width: '100px',
      wrap: true,
    },
    {
      name: 'No. of Deliveries Completed',
      selector: (row) => row.deliveriesCompleted,
      sortable: true,
      right: true,
      width: '180px',
      wrap: true,
    },
  ];

  // New Driver Report Columns
  const driverColumns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      width: '60px',
      compact: true,
    },
    {
      name: 'Branch Name',
      selector: (row) => row.branchName,
      sortable: true,
      width: '120px',
      wrap: true,
    },
    {
      name: 'Delivery Date',
      selector: (row) => row.deliveryDate,
      sortable: true,
      width: '120px',
      wrap: true,
      // Sorting is handled manually in useMemo based on ISO date for accuracy
    },
    {
      name: 'Driver Name',
      selector: (row) => row.driverName,
      sortable: true,
      width: '120px',
      wrap: true,
    },
    {
      name: 'Deliveries Count',
      selector: (row) => row.deliveriesCount,
      sortable: true,
      right: true,
      width: '130px',
      wrap: true,
    },
    {
      name: 'Avg Delivery Time',
      selector: (row) => row.avgDeliveryTime,
      sortable: false, // Keeping average time non-sortable as it's complex
      right: true,
      width: '140px',
      wrap: true,
    },
  ];

  const orderColumns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      width: '60px',
      compact: true,
    },
    {
      name: 'PO Number',
      selector: (row) => row.po_number,
      sortable: true,
      width: '100px',
      wrap: true,
    },
    {
      name: 'Order Type',
      selector: (row) => row.type,
      sortable: true,
      width: '120px',
      wrap: true,
    },
    {
      name: 'Showroom',
      selector: (row) => row.showroom?.name || 'N/A',
      sortable: true,
      width: '120px',
      wrap: true,
    },
    {
      name: 'Driver',
      selector: (row) => row.driver?.name || 'N/A',
      sortable: true,
      width: '120px',
      wrap: true,
    },
    {
      name: 'Customer/Vendor',
      selector: (row) => row.customer?.name || row.vendor?.name || 'N/A',
      sortable: true,
      width: '140px',
      wrap: true,
      style: {
        wordBreak: 'break-word',
      },
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      width: '100px',
      wrap: true,
    },
    {
      name: 'Order Created At',
      selector: (row) => formatDateTime12Hr(row.created_at),
      sortable: true,
      width: '150px',
      wrap: true,
    },
    {
      name: 'Order Updated At',
      selector: (row) => formatDateTime12Hr(row.updated_at),
      sortable: true,
      width: '150px',
      wrap: true,
    },
    {
      name: 'Time Taken',
      selector: (row) =>
        calculateTimeTaken(
          row.status_logs?.find((l) => l.to_status === 'picked_up')?.created_at,
          row.status_logs?.find((l) => l.to_status === 'delivered')?.created_at
        ) || 'N/A',
      sortable: true,
      width: '120px',
      wrap: true,
    },
  ];

  const getTableData = () => {
    switch (selectedRole) {
      case 'driver':
        return { data: groupedByDriver, columns: driverColumns };
      case 'vendor':
        return { data: groupedByVendor, columns: vendorColumns };
      default:
        return { data: orders, columns: orderColumns };
    }
  };

  const { data: tableData, columns: tableColumns } = getTableData();

  // Custom Loading Component for DataTable
  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>
        {currentPage && lastPage && lastPage > 1
          ? `Loading orders... (Page ${currentPage} of ${lastPage})`
          : 'Loading orders...'}
      </span>
    </div>
  );

  // Custom No Data Component for DataTable
  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaBoxOpen className='mb-2' size={32} />
      <h5 className='mt-2'>No Data Found</h5>
      <p className='mb-0'>
        There is currently no data in this report or matching your search
        criteria.
      </p>
    </div>
  );

  return (
    <div className='border p-2 rounded' style={{ background: '#fff' }}>
      <Container fluid className='p-4'>
        <h3 style={{ textAlign: 'center' }}>Order History / Reports</h3>

        {/* Filters */}
        <Row className='justify-content-between align-items-center mt-3 mb-3'>
          <Col md={8}>
            <Row className='g-2'>
              <Col xs='auto'>
                <InputGroup style={{ width: '300px' }}>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder='Search...'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col xs='auto' style={{ width: '200px' }}>
                <Select
                  options={[
                    { value: null, label: 'All Status' },
                    ...statusOptions,
                  ]}
                  onChange={(opt) => setSelectedStatus(opt)}
                  value={selectedStatus}
                  isClearable
                  placeholder='Filter Status...'
                />
              </Col>
              <Col xs='auto' style={{ width: '200px' }}>
                <Select
                  options={showrooms}
                  onChange={(opt) => setSelectedShowroom(opt)}
                  value={selectedShowroom}
                  isClearable
                  placeholder='Filter Showroom...'
                />
              </Col>
              <Col xs='auto'>
                <div className='d-flex align-items-center gap-2'>
                  <Form.Control
                    type='date'
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    title='Date From (Order Created At)'
                    style={{ width: '150px' }}
                  />
                  <span>to</span>
                  <Form.Control
                    type='date'
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    title='Date To (Order Created At)'
                    style={{ width: '150px' }}
                  />
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    <FaSearch className='me-1' />
                    Search
                  </Button>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleClearFilters}
                    disabled={loading}
                  >
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>

          <Col md={4} className='text-end'>
            <div className='d-flex gap-2 justify-content-end'>
              {['driver', 'vendor', 'customer'].map((role) => (
                <Button
                  key={role}
                  size='sm'
                  variant={
                    selectedRole === role ? 'primary' : 'outline-primary'
                  }
                  onClick={() => setSelectedRole(role)}
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </Button>
              ))}
              <Button
                size='sm'
                variant={
                  selectedRole === null ? 'secondary' : 'outline-secondary'
                }
                onClick={() => setSelectedRole(null)}
              >
                All Orders
              </Button>
              <Button size='sm' variant='success' onClick={handleExportToExcel}>
                <FaFileExcel /> Export
              </Button>
            </div>
          </Col>
        </Row>

        {/* Table */}
        <div style={{ maxHeight: '53vh', position: 'relative' }}>
          <DataTable
            columns={tableColumns}
            data={tableData}
            pagination={false}
            highlightOnHover
            striped
            dense
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight='53vh'
            noTableHead={false}
            customStyles={{
              table: {
                style: {
                  tableLayout: 'fixed',
                  width: '100%',
                  maxWidth: '100%',
                },
              },
              tableWrapper: {
                style: {
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'hidden',
                },
              },
              headRow: {
                style: {
                  backgroundColor: '#f8f9fa',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: '2px',
                  borderBottomColor: '#dee2e6',
                },
              },
              headCells: {
                style: {
                  fontSize: '14px',
                  fontWeight: '600',
                  // textTransform: 'uppercase',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                  '&:focus': {
                    outline: 'none',
                  },
                },
              },
              cells: {
                style: {
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                },
              },
            }}
            progressPending={loading}
            progressComponent={CustomLoadingIndicator}
            noDataComponent={CustomNoDataComponent}
          />
          {!loading && allOrders.length > 0 && (
            <div className='text-center p-3 text-muted'>
              <small>All orders loaded ({allOrders.length} total)</small>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default AdminReports;
